import { Button, Cell, Grid, Heading, Icon, Tooltip, VFlow } from 'bold-ui'
import { CiapSelectField, CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { Form, FormRenderProps } from 'components/form/final-form/Form'
import { HLabel } from 'components/HLabel'
import { FormApi } from 'final-form'
import { CiapCapituloEnum } from 'graphql/types.generated'
import React, { useState } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { isEmpty } from 'util/validation/Util'
import { getEditableItemRemoveTooltipText } from 'view/atendimentos/atendimento-individual/atendimento-observacao/util-atendObservacao'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { EditableList, EditableRow, useEditableListField } from '../../../components/EditableList'
import { Observacao } from '../../../components/Observacao'
import { ObservacaoFormGrid } from '../../../components/ObservacaoFormGrid'

export interface CiapPlanoModel extends SoapEditableItem {
  _id: ID
  ciap: CiapSelectFieldModel
  observacao?: string
}

const meta = metaPath<CiapPlanoModel>()

export interface CiapPlanoProps {
  name: MetaArray<CiapPlanoModel>
  cidadao?: CidadaoAtendimento
}

const validator = createValidator<CiapPlanoModel>({
  observacao: [maxLength(200)],
})

export function CiapPlanoField(props: CiapPlanoProps) {
  const { name, cidadao } = props

  const {
    handleRowChanged,
    handleSubmit,
    input: { value },
    removeItem,
  } = useEditableListField({ name })

  const ciapIsEqual = (ciap: CiapSelectFieldModel) => {
    return (value || []).find((o2) => ciap.id === o2.ciap.id)
  }

  const renderForm = (formProps: FormRenderProps<CiapPlanoModel>) => {
    const handleChange = (newValue: CiapSelectFieldModel) => {
      newValue && formProps.form.submit()
    }

    return (
      <CiapSelectField
        label='CIAP 2'
        onChange={handleChange}
        name={meta.ciap}
        itemIsEqual={ciapIsEqual}
        sexo={isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : undefined}
        capitulosIncluidos={[CiapCapituloEnum.PROCEDIMENTOS]}
        menuMinWidth={620}
        excluirCIAPsDAB
        fetchCID10Encaminhamento
        dataTestId='CiapPlano'
      />
    )
  }

  return (
    <Grid>
      <Cell size={8}>
        <Form<CiapPlanoModel> render={renderForm} validate={validator} onSubmit={handleSubmit} />
      </Cell>
      {value?.length > 0 && (
        <Cell size={12}>
          <EditableList>
            {value.map((item) => (
              <CiapRow key={item._id} model={item} onChange={handleRowChanged} onRemove={removeItem} />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

interface CiapRowProps {
  model: CiapPlanoModel
  onChange(values: CiapPlanoModel): void
  onRemove(itemToRemove: CiapPlanoModel): void
}

function CiapRow(props: CiapRowProps) {
  const { model, onChange, onRemove } = props
  const [editing, setEditing] = useState(false)

  const { isEditable, tooltipText: removeTooltipText } = getEditableItemRemoveTooltipText(model)

  const editTooltipText = model.observacao ? 'Editar observação' : 'Adicionar observação'

  const toggleEditing = () => setEditing((oldState) => !oldState)

  const handleRemove = () => onRemove(model)

  const handleSubmit = (value: CiapPlanoModel, formApi: FormApi) => {
    onChange(value)
    setTimeout(formApi.reset)
    toggleEditing()
  }

  const renderForm = (formRenderProps: FormRenderProps<CiapPlanoModel>) => (
    <VFlow vSpacing={0.5} style={{ padding: '1rem 0' }}>
      <Heading level={4}>Adicionar observação</Heading>
      <HLabel title='CIAP 2:'>{model.ciap.descricao}</HLabel>
      <ObservacaoFormGrid name={meta.observacao} onCancel={toggleEditing} onSubmit={formRenderProps.handleSubmit} />
    </VFlow>
  )

  return (
    <EditableRow editing={editing}>
      {!editing && (
        <>
          <Grid style={{ margin: 0 }}>
            <Cell size={9} alignSelf='center'>{`${model.ciap.descricao} - ${model.ciap.codigo}`}</Cell>
            <Cell size={3} alignSelf='center' style={{ padding: '0.25rem 0', textAlign: 'right' }}>
              <Tooltip text={editTooltipText}>
                <Button size='small' skin='ghost' onClick={toggleEditing}>
                  <Icon icon='chatOutline' />
                </Button>
              </Tooltip>
              <Tooltip text={removeTooltipText}>
                <Button size='small' skin='ghost' onClick={handleRemove} disabled={isEditable}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </Cell>
          </Grid>
          <Observacao observacao={model.observacao} />
        </>
      )}
      {editing && (
        <Form<CiapPlanoModel>
          render={renderForm}
          initialValues={model}
          onSubmit={handleSubmit}
          validate={validator}
          initialValuesEqual={(oldInitial, newInitial) => oldInitial._id === newInitial._id}
        />
      )}
    </EditableRow>
  )
}
